import { useContext, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Checkbox from '@mui/material/Checkbox';
import Line from '../../../Utilities/Line';
import { SessionContext } from '../../../Contexts/SessionContext';
import DropDownViewModel from '../../../Utilities/ViewModels/DropDownViewModel';
import { getFilteredSurveys } from '../../../TripRateNew/TriprateFilters';
import { SurveyFullViewModel } from '../../../TripRateNew/ViewModels/SurveyFullViewModel';
import { FetchAction, useTripRateContext } from '../../TripRateContext';
import useData from '../../../Utilities/Hooks/UseData';
import { SessionState } from '../../../Contexts/SessionState';
import { DotNestedKeys } from '../../../Utilities/Types/SessionType';
import WarningPopup from '../../../Popups/WarningPopup';
import CompactTable from '../../../Utilities/CompactTable';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { CounterBadge } from '../../../Utilities/CounterBadge';


interface IProps {
    title: string;
    heading: string;
    fetchAction: FetchAction;
    path: DotNestedKeys<SessionState>;
    id: keyof SurveyFullViewModel;
    alertCondition?: (items: DropDownViewModel[], selected: any[]) => boolean;
    canCollapseZeroCounts?: boolean;
    badgeNumber?: string;
    isCompressedSummary?: boolean;
    fetchParams?: string;
    disabled?: boolean;
}

export function getValueAtPath<T>(obj: any, path: string): T | undefined {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
}

export function SecondaryFilterTickBox({
    fetchAction,
    title,
    path,
    id,
    alertCondition,
    canCollapseZeroCounts = false,
    badgeNumber, isCompressedSummary = false,
    fetchParams,
    disabled = false
}: IProps) {
    const { data: baseDropdown } = useData<DropDownViewModel[]>('TripRateFilterNew', fetchAction + (fetchParams ? fetchParams : ""));

    const { surveys } = useTripRateContext();
    const { sessionState, setSessionField } = useContext(SessionContext);
    const selected = getValueAtPath<any[]>(sessionState, path) ?? [];
    const [showZeroCounts, setShowZeroCounts] = useState(false);

    const filteredSurveys = useMemo(
        () =>
            getFilteredSurveys(
                surveys,
                { fetchAction }
            ),
        [surveys, sessionState, fetchAction]
    );

    const dropdown = useMemo(() => {
        if (!baseDropdown?.length)
            return baseDropdown ?? [];

        return baseDropdown.map((x) => {
            const count = filteredSurveys?.filter((s) => {
                const surveyValue = fetchAction === 'GetCarOwnershipFiveMile'
                    ? ((s as any)[id] ?? 0)
                    : (s as any)[id];
                return surveyValue === x.id;
            }).length ?? 0;
            const cleanedLabel = x.label?.replace(/\s*\(\d+\)$/, '').trim();
            return { ...x, label: `${cleanedLabel} (${count})` };
        });
    }, [filteredSurveys, baseDropdown, id, fetchAction]);

    const parsedDropdown = useMemo(() => {
        const parseRowData = (label: string, isSecondaryUseClass: boolean) => {
            const parts = label.split(' ');
            if (label.includes('1a (Low) - Very poor')) {
                return { name: '1a (low)', count: parts[5]?.replace(/[()]/g, '') };
            }
            if (!isSecondaryUseClass) {
                const [name, countPart] = label.split('(');
                return { name: name?.trim() || '', count: countPart?.split(')')[0] || '' };
            }
            const isSpecialCase =
                label.includes('Not Known') || label.includes('Sui Generis');
            if (isSpecialCase) {
                return {
                    name: `${parts[0]} ${parts[1]}`,
                    count: parts[2]?.replace(/[()]/g, '') || '',
                };
            }
            return { name: parts[0] || '', count: parts[1]?.replace(/[()]/g, '') || '' };
        };

        return dropdown.map((row) => ({
            ...row,
            ...parseRowData(row.label, fetchAction === 'GetSecondaryUseClass'),
        }));
    }, [dropdown, fetchAction]);

    const handleChangeString = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const id = parsedDropdown[index].id;
        const selectedIds = selected.filter((x) => x != null).map(String);
        const updated = e.target.checked
            ? [...selectedIds, String(id)]
            : selectedIds.filter((x) => x !== String(id));

        setSessionField(path, updated);
    };

    const handleChangeNumber = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const id = parsedDropdown[index].id;
        const selectedIds = selected.filter((x) => x != null).map(Number);
        const updated = e.target.checked
            ? [...selectedIds, Number(id)]
            : selectedIds.filter((x) => x !== Number(id));

        setSessionField(path, updated);
    };


    const handleChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        if (fetchAction === 'GetPetrolFillingStation') {
            return handleChangeString(index)(e);
        } else {
            return handleChangeNumber(index)(e);
        }
    }

    const isSelected = (id: number) => selected.includes(id);

    const alert = useMemo(() => {
        if (!alertCondition || !parsedDropdown?.length) return false;
        return alertCondition(parsedDropdown, selected);
    }, [alertCondition, parsedDropdown, selected]);

    const visibleRows = useMemo(() => {
        if (!canCollapseZeroCounts) return parsedDropdown;
        if (!showZeroCounts)
            return parsedDropdown.filter((r) => Number(r.count) > 0);
        return parsedDropdown;
    }, [parsedDropdown, showZeroCounts, canCollapseZeroCounts]);

    return (
        <Grid
            container
            size={12}
            component={(isCompressedSummary ? 'div' : Paper) as React.ElementType}
            sx={{
                position: 'relative',
            }}
        >
            {!isCompressedSummary &&
                <>
                    {badgeNumber &&
                        <CounterBadge
                            value={badgeNumber}
                        />
                    }
                    <Grid
                        container
                        size={12}
                        alignItems="center"
                        justifyContent="center"
                        sx={{ position: 'relative' }}

                    >
                        {/* Centered Title */}
                        <Typography textAlign="center" sx={{ fontWeight: 500 }}>
                            {title}
                        </Typography>

                        {/* Optional collapse icon, pinned to the top-left */}
                        {canCollapseZeroCounts && (
                            <IconButton
                                onClick={() => setShowZeroCounts((v) => !v)}
                                color="primary"
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: -1,
                                    transform: 'none',
                                }}
                            >
                                {showZeroCounts ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        )}

                    </Grid>
                    <Line />
                </>
            }
            <Grid size={12}>
                <TableContainer>
                    <CompactTable>
                        <TableHead>
                            <TableRow>
                                <TableCell> {isCompressedSummary && title}</TableCell>
                                <TableCell>Count</TableCell>
                                <TableCell>
                                    Include
                                    {alert && (
                                        <WarningPopup
                                            title="Good Practice Guidance Advice - Warning on Location Type Selections"
                                            content="Non-recommended combinations of main location types are currently selected. Please see the TRICS Good Practice Guide."
                                            type="error"
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows.map((row, i) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{row.count}</TableCell>
                                    <TableCell>
                                        <Checkbox
                                            size="medium"
                                            checked={isSelected(row.id)}
                                            onChange={handleChange(i)}
                                            disabled={disabled}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </CompactTable>
                </TableContainer>
            </Grid>
        </Grid>
    );
}
